import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Contract } from "ethers";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import TabStake from "./staking/StakeTab";
import TabUnstake from "./staking/UnstakeTab";
import TransactionModal from "../../../modals/TransactionModal";
import {
  ADDRESSES,
  stakingRewardsAbi,
  VALID_SYMBOLS,
  TokenSymbol,
} from "../../../config/contracts";
import { useSmartWalletSigner } from "../../../hooks/useSmartWalletSigner";
import { useWaitForTransactionReceipt } from "wagmi";

export type Balances = Record<TokenSymbol, number>;

interface DashboardData {
  staked_balances: Balances;
  pending_rewards: Balances;
  reward_rates: Balances;
}

interface TabStakingProps {
  setBalanceRefresh: React.Dispatch<React.SetStateAction<number>>;
}

const formatNumber = (value: string | number): string => {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return isNaN(n) ? "N/A" : n.toFixed(6);
};

const TabStaking: React.FC<TabStakingProps> = ({ setBalanceRefresh }) => {
  const [activeTab, setActiveTab] = useState<"stake" | "unstake">("stake");
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);

  const [claimTxHash, setClaimTxHash] = useState<`0x${string}` | undefined>();
  const [claimingToken, setClaimingToken] = useState<TokenSymbol | null>(null);

  const { signer, isConnected, address } = useSmartWalletSigner();
  const { isLoading: isClaimPending, isSuccess: isClaimConfirmed } =
    useWaitForTransactionReceipt({ hash: claimTxHash });

  const parseErrorMessage = (error: unknown): string => {
    const message =
      typeof error === "string" ? error : (error as Error)?.message || "";

    if (message.includes("insufficient funds"))
      return "Not enough ETH to pay for gas fees.";
    if (
      message.includes("execution reverted") &&
      message.includes("No rewards")
    )
      return "You don't have any rewards available to claim.";
    if (message.includes("execution reverted"))
      return "Transaction reverted. Please check your staking status.";
    if (message.includes("user rejected"))
      return "Transaction cancelled by the user.";
    if (message.includes("not authorized"))
      return "You are not authorized to perform this action.";
    if (message.includes("estimateGas"))
      return "Gas estimation failed. The contract may have rejected the transaction.";
    return "An unexpected error occurred while claiming rewards.";
  };

  const fetchDashboard = useCallback(async () => {
    if (!address) return;
    try {
      const res = await axios.get(
        "https://site--ucwaves-p1-staking-deployement--r9qw5p2frw6c.code.run/user/dashboard",
        { headers: { "x-wallet-address": address } }
      );
      setDashboard(res.data.data);
    } catch {
      toast.error("Failed to load dashboard data.");
    } finally {
      setIsLoadingDashboard(false);
    }
  }, [address]);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 20000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  useEffect(() => {
    if (isClaimConfirmed && claimingToken) {
      setClaimingToken(null);
      fetchDashboard();
      setBalanceRefresh((prev) => prev + 1);
    }
  }, [isClaimConfirmed, claimingToken, fetchDashboard, setBalanceRefresh]);

  const handleClaim = useCallback(
    async (symbol: TokenSymbol) => {
      if (!isConnected || !address || isClaimPending || !signer) {
        toast.error("Wallet not connected or signer unavailable.");
        return;
      }

      const reward = dashboard?.pending_rewards[symbol];
      const numericReward =
        typeof reward === "string" ? parseFloat(reward) : Number(reward ?? 0);

      if (numericReward <= 0.000001) {
        toast.warning("No rewards available to claim.");
        return;
      }

      setClaimingToken(symbol);
      setClaimTxHash(undefined);

      const stakingAddress =
        ADDRESSES[`${symbol}_STAKING` as keyof typeof ADDRESSES];

      if (!stakingAddress) {
        toast.error("Staking contract address not found.");
        setClaimingToken(null);
        return;
      }

      try {
        const contract = new Contract(
          stakingAddress,
          stakingRewardsAbi,
          signer
        );
        const tx = await contract.claimRewards();
        setClaimTxHash(tx.hash);
      } catch (err) {
        const message = parseErrorMessage(err);
        toast.error(message);
        setClaimingToken(null);
      }
    },
    [isConnected, address, isClaimPending, signer, dashboard]
  );

  if (isLoadingDashboard || !dashboard) {
    return (
      <div className="flex flex-col items-center justify-center p-6 w-full max-w-md mx-auto h-140 text-white font-outfit">
        <div className="w-8 h-8 border-4 border-red-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-l text-gray-200">Loading staking dashboard...</p>
      </div>
    );
  }

  return (
    <div className="text-red-800 font-outfit pt-2 pb-2 space-y-3 w-full max-w-5xl mx-auto text-center">
      <h1 className="text-xl">Stake Your Influence. Earn Rewards.</h1>
      <p className=" text-gray-700 font-bold mb-2">
        Lock NAU, NAUY, or NAUN to secure the network and earn passive rewards.
        Your staked balance will continuously generate returns based on protocol
        activity and token weight.
      </p>
      <ToastContainer position="top-right" autoClose={4000} />
      <AnimatePresence>
        {isClaimPending && (
          <TransactionModal
            txStatus={isClaimConfirmed ? "confirmed" : "pending"}
            txHash={claimTxHash ?? null}
            userAddress={address ?? null}
            closeModal={() => {
              setClaimTxHash(undefined);
              setClaimingToken(null);
            }}
          />
        )}
      </AnimatePresence>

      {/* Dashboard Overview */}
      <div className="flex flex-col  md:justify-between gap-4 bg-white/10 border border-white/20 p-4 backdrop-blur-lg max-h-[80%]">
        <div className="flex flex-col md:flex-row ">
          {/* Staked */}
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-bold text-base text-center mb-2 border-b border-white/30 pb-1">
              Staked Balances
            </h3>
            <ul className="space-y-1 flex flex-col justify-center items-center">
              {VALID_SYMBOLS.map((sym) => (
                <li key={sym}>
                  • {sym}: {formatNumber(dashboard.staked_balances[sym])}
                </li>
              ))}
            </ul>
          </div>
          {/* Rewards */}
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-bold text-base text-center mb-2 border-b border-white/30 pb-1">
              Pending Rewards
            </h3>
            <ul className="space-y-4 flex flex-col justify-center items-center">
              {VALID_SYMBOLS.map((sym) => {
                const reward = dashboard.pending_rewards[sym];
                const numericReward =
                  typeof reward === "string"
                    ? parseFloat(reward)
                    : Number(reward ?? 0);
                const canClaim =
                  !isNaN(numericReward) && numericReward > 0.000001;
                const isClaiming = claimingToken === sym;

                return (
                  <li key={sym} className="flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{sym}:</span>
                      <motion.span
                        key={sym}
                        initial={{ opacity: 0.5 }}
                        animate={
                          canClaim
                            ? { opacity: 1, scale: [1, 1.2, 1] }
                            : { opacity: 1 }
                        }
                        transition={{ duration: 1, repeat: Infinity }}
                        className="text-red-800/80"
                      >
                        {formatNumber(numericReward)}
                      </motion.span>
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleClaim(sym)}
                      disabled={!canClaim || isClaimPending}
                      className={`w-24 py-1 text-sm font-semibold transition disabled:opacity-50 ${
                        isClaiming
                          ? "bg-green-300 text-black cursor-wait"
                          : "bg-green-600 hover:bg-green-800 text-white"
                      }`}
                    >
                      {isClaiming ? (
                        <span className="animate-spin">⏳</span>
                      ) : (
                        "Claim"
                      )}
                    </motion.button>
                  </li>
                );
              })}
            </ul>
          </div>
          {/* Rates */}
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-bold text-base text-center mb-2 border-b border-white/30 pb-1">
              Reward Rates*
            </h3>
            <ul className="space-y-1 flex flex-col justify-center items-center">
              {VALID_SYMBOLS.map((sym) => (
                <li key={sym}>
                  • {sym}: {formatNumber(dashboard.reward_rates[sym])}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className=" text-gray-700 font-bold mb-2">
          Rewards can be claimed at any time — there’s no lockup period.
          However, staked tokens are subject to a 7-day lockup before they can
          be unstaked.
        </p>
        <p className=" text-red-9500  text-start font-bold mb-2">
          *The reward rate reflects the number of Tokens distributed per second,
          prorated across all stakers based on their share of the total Pool
          Staked .
        </p>
      </div>
      {/* Tabs */}
      <div className="flex justify-center gap-4 mt-4">
        {(["stake", "unstake"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 w-32 font-medium text-sm ${
              activeTab === tab
                ? "bg-white text-red-600 shadow"
                : "bg-white/20 text-white hover:bg-white/30"
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      <p className=" text-gray-700 font-bold mb-2">
        Adding more tokens will reset the 7-day lockup period for your entire
        stake, including previously staked tokens. If you need to unstake soon,
        consider unstaking first, then restaking the total amount.
      </p>

      {/* Tab content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {activeTab === "stake" ? (
          <TabStake
            balances={dashboard.staked_balances}
            setBalances={(b) =>
              setDashboard((prev) =>
                prev ? { ...prev, staked_balances: b } : prev
              )
            }
            fetchDashboard={fetchDashboard}
            setBalanceRefresh={setBalanceRefresh}
          />
        ) : (
          <TabUnstake
            balances={dashboard.staked_balances}
            setBalances={(b) =>
              setDashboard((prev) =>
                prev ? { ...prev, staked_balances: b } : prev
              )
            }
            setBalanceRefresh={setBalanceRefresh}
          />
        )}
      </motion.div>
    </div>
  );
};

export default TabStaking;
